import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  UserRound,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { State, City } from "country-state-city";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import type { CandidateApplicationData } from "@/modules/candidate/types/candidateApplication.types";

type ProfileField =
  | "fullName"
  | "email"
  | "phone"
  | "currentLocation"
  | "currentCompany"
  | "currentDesignation"
  | "totalExperience"
  | "highestQualification"
  | "currentSalary"
  | "expectedSalary"
  | "noticePeriod"
  | "noticePeriod"
  | "linkedinUrl"
  | "portfolioUrl";

interface ProfileReviewStepProps {
  candidateProfile: CandidateApplicationData;
  profileError: string | null;
  profileFieldErrors?: Partial<Record<ProfileField, string>>;
  onFieldChange: (
    field: keyof CandidateApplicationData,
    value: string
  ) => void;
  onBack: () => void;
  onNext: () => void;
}

export default function ProfileReviewStep({
  candidateProfile,
  profileError,
  profileFieldErrors = {},
  onFieldChange,
  onBack,
  onNext,
}: ProfileReviewStepProps) {
  const fieldRefs = useRef<
    Partial<Record<ProfileField, HTMLElement | null>>
  >({});

  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  const states = State.getStatesOfCountry("IN");

  const cities = selectedState
    ? City.getCitiesOfState("IN", selectedState)
    : [];

  useEffect(() => {
    const location = candidateProfile.currentLocation?.trim();

    if (!location) {
      setSelectedState("");
      setSelectedCity("");
      return;
    }

    const parts = location
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);

    const cityName = parts[0] || "";
    const stateValue = parts[1] || "";

    const matchedState = states.find(
      (state) =>
        state.isoCode.toLowerCase() === stateValue.toLowerCase() ||
        state.name.toLowerCase() === stateValue.toLowerCase()
    );

    if (!matchedState) {
      console.log(
        "State not matched:",
        stateValue,
        "Available location:",
        location
      );
      return;
    }

    const stateCities = City.getCitiesOfState(
      "IN",
      matchedState.isoCode
    );

    const matchedCity = stateCities.find(
      (city) =>
        city.name.toLowerCase() === cityName.toLowerCase()
    );

    setSelectedState(matchedState.isoCode);
    setSelectedCity(matchedCity?.name || cityName);
  }, [candidateProfile.currentLocation]);


  useEffect(() => {
    const firstInvalidField = Object.keys(profileFieldErrors)[0] as
      | ProfileField
      | undefined;

    if (!firstInvalidField) {
      return;
    }

    const element = fieldRefs.current[firstInvalidField];

    if (!element) {
      return;
    }

    setTimeout(() => {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      if (element instanceof HTMLElement) {
        element.focus();
      }
    }, 100);
  }, [profileFieldErrors]);

  const getFieldClassName = (field: ProfileField) => {
    return profileFieldErrors[field]
      ? "border-red-500 focus-visible:border-red-500 focus-visible:ring-red-500"
      : "";
  };

  const renderFieldError = (field: ProfileField) => {
    const error = profileFieldErrors[field];

    if (!error) {
      return null;
    }

    return (
      <p className="flex items-center gap-1 text-sm text-red-600">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        {error}
      </p>
    );
  };

  return (
    <div className="px-6 py-7">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-50">
            <UserRound className="h-6 w-6 text-blue-600" />
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Review Your Profile
            </h2>

            <p className="mt-1 text-sm leading-6 text-gray-500">
              Review the extracted information and complete any missing
              details before continuing.
            </p>
          </div>
        </div>

        <div className="mt-7 grid gap-5 md:grid-cols-2">
          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="candidate-name">
              Full Name <span className="text-red-500">*</span>
            </Label>

            <Input
              ref={(element) => {
                fieldRefs.current.fullName = element;
              }}
              id="candidate-name"
              value={candidateProfile.fullName}
              placeholder="Enter your full name"
              className={getFieldClassName("fullName")}
              onChange={(event) =>
                onFieldChange("fullName", event.target.value)
              }
            />

            {renderFieldError("fullName")}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="candidate-email">
              Email Address <span className="text-red-500">*</span>
            </Label>

            <Input
              ref={(element) => {
                fieldRefs.current.email = element;
              }}
              id="candidate-email"
              type="email"
              value={candidateProfile.email}
              placeholder="you@example.com"
              className={getFieldClassName("email")}
              onChange={(event) =>
                onFieldChange("email", event.target.value)
              }
            />

            {renderFieldError("email")}
          </div>

          {/* Phone Number */}
          <div className="space-y-2">
            <Label htmlFor="candidate-phone">
              Phone Number <span className="text-red-500">*</span>
            </Label>

            <Input
              ref={(element) => {
                fieldRefs.current.phone = element;
              }}
              id="candidate-phone"
              type="tel"
              value={candidateProfile.phone}
              placeholder="+91 98765 43210"
              className={getFieldClassName("phone")}
              onChange={(event) =>
                onFieldChange("phone", event.target.value)
              }
            />

            {renderFieldError("phone")}
          </div>

          {/* Country */}
          <div className="space-y-2">
            <Label htmlFor="country">
              Country
            </Label>

            <Input
              id="country"
              value="India"
              readOnly
              className="bg-white text-slate-900"
            />
          </div>

          {/* State */}
          <div className="space-y-2">
            <Label htmlFor="candidate-state">
              State <span className="text-red-500">*</span>
            </Label>

            <select
              ref={(element) => {
                fieldRefs.current.currentLocation = element;
              }}
              id="candidate-state"
              value={selectedState}
              onChange={(e) => {
                const stateCode = e.target.value;

                setSelectedState(stateCode);
                setSelectedCity("");

                onFieldChange("currentLocation", "");
              }}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm"
            >
              <option value="">Select State</option>

              {states.map((state) => (
                <option key={state.isoCode} value={state.isoCode}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          {/* City */}
          <div className="space-y-2">
            <Label htmlFor="candidate-city">
              City <span className="text-red-500">*</span>
            </Label>

            <select
              id="candidate-city"
              value={selectedCity}
              disabled={!selectedState}
              onChange={(e) => {
                const cityName = e.target.value;

                setSelectedCity(cityName);

                const state = states.find(
                  (item) => item.isoCode === selectedState
                );

                onFieldChange(
                  "currentLocation",
                  cityName && state
                    ? `${cityName}, ${state.name}, India`
                    : ""
                );
              }}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm disabled:bg-slate-100"
            >
              <option value="">
                {selectedState ? "Select City" : "Select State First"}
              </option>

              {cities.map((city) => (
                <option key={city.name} value={city.name}>
                  {city.name}
                </option>
              ))}
            </select>

            {renderFieldError("currentLocation")}
          </div>
          {/* Total Experience */}
          <div className="space-y-2">
            <Label>
              Total Experience <span className="text-red-500">*</span>
            </Label>

            <Select
              value={candidateProfile.totalExperience}
              onValueChange={(value) =>
                onFieldChange("totalExperience", value)
              }
            >
              <SelectTrigger
                ref={(element) => {
                  fieldRefs.current.totalExperience = element;
                }}
                className={getFieldClassName("totalExperience")}
              >
                <SelectValue placeholder="Select experience" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="Fresher">Fresher</SelectItem>
                <SelectItem value="Less than 1 year">
                  Less than 1 year
                </SelectItem>
                <SelectItem value="1-2 years">1–2 years</SelectItem>
                <SelectItem value="2-3 years">2–3 years</SelectItem>
                <SelectItem value="3-5 years">3–5 years</SelectItem>
                <SelectItem value="5-8 years">5–8 years</SelectItem>
                <SelectItem value="8-10 years">8–10 years</SelectItem>
                <SelectItem value="10+ years">10+ years</SelectItem>
              </SelectContent>
            </Select>

            {renderFieldError("totalExperience")}
          </div>

          {/* LinkedIn - Optional */}
          <div className="space-y-2">
            <Label htmlFor="candidate-linkedin">
              LinkedIn Profile<span className="text-slate-400">(Optional)</span>
            </Label>

            <Input
              id="candidate-linkedin"
              type="url"
              value={candidateProfile.linkedinUrl}
              placeholder="https://linkedin.com/in/..."
              onChange={(event) =>
                onFieldChange("linkedinUrl", event.target.value)
              }
            />
            {profileFieldErrors.linkedinUrl && (
              <p className="text-sm text-red-500">
                {profileFieldErrors.linkedinUrl}
              </p>
            )}
          </div>

          {/* Notice Period */}
          <div className="space-y-2">
            <Label>
              Notice Period
            </Label>

            <Select
              value={candidateProfile.noticePeriod}
              onValueChange={(value) =>
                onFieldChange("noticePeriod", value)
              }
            >
              <SelectTrigger
                ref={(element) => {
                  fieldRefs.current.noticePeriod = element;
                }}
                className={getFieldClassName("noticePeriod")}
              >
                <SelectValue placeholder="Select notice period" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="N/A">N/A</SelectItem>
                <SelectItem value="Immediate">Immediate</SelectItem>
                <SelectItem value="15 days">15 days</SelectItem>
                <SelectItem value="30 days">30 days</SelectItem>
                <SelectItem value="45 days">45 days</SelectItem>
                <SelectItem value="60 days">60 days</SelectItem>
                <SelectItem value="90 days">90 days</SelectItem>
              </SelectContent>
            </Select>

            {renderFieldError("noticePeriod")}
          </div>

          {/* Current CTC */}
          <div className="space-y-2">
            <Label htmlFor="current-ctc">
              Current CTC
            </Label>

            <Input
              ref={(element) => {
                fieldRefs.current.currentSalary = element;
              }}
              id="current-ctc"
              type="number"
              min="0"
              step="any"
              value={candidateProfile.currentSalary}
              placeholder="Example: 8.5"
              className={getFieldClassName("currentSalary")}
              onChange={(event) => {
                const value = event.target.value;

                if (value === "" || Number(value) >= 0) {
                  onFieldChange("currentSalary", value);
                }
              }}
            />

            {renderFieldError("currentSalary")}
          </div>

          {/* Expected CTC */}
          <div className="space-y-2">
            <Label htmlFor="expected-ctc">
              Expected CTC
            </Label>

            <Input
              ref={(element) => {
                fieldRefs.current.expectedSalary = element;
              }}
              id="expected-ctc"
              type="number"
              min="0"
              step="any"
              value={candidateProfile.expectedSalary}
              placeholder="Example: 12"
              className={getFieldClassName("expectedSalary")}
              onChange={(event) => {
                const value = event.target.value;

                if (value === "" || Number(value) >= 0) {
                  onFieldChange("expectedSalary", value);
                }
              }}
            />

            {renderFieldError("expectedSalary")}
          </div>

          {/* Current Company */}
          <div className="space-y-2">
            <Label htmlFor="candidate-company">
              Current Company <span className="text-red-500">*</span>
            </Label>

            <Input
              ref={(element) => {
                fieldRefs.current.currentCompany = element;
              }}
              id="candidate-company"
              value={candidateProfile.currentCompany}
              placeholder="Enter current company or N/A"
              className={getFieldClassName("currentCompany")}
              onChange={(event) =>
                onFieldChange("currentCompany", event.target.value)
              }
            />

            {renderFieldError("currentCompany")}
          </div>

          {/* Current Designation */}
          <div className="space-y-2">
            <Label htmlFor="candidate-designation">
              Current Designation{" "}
              <span className="text-red-500">*</span>
            </Label>

            <Input
              ref={(element) => {
                fieldRefs.current.currentDesignation = element;
              }}
              id="candidate-designation"
              value={candidateProfile.currentDesignation}
              placeholder="Software Engineer or fresher"
              className={getFieldClassName("currentDesignation")}
              onChange={(event) =>
                onFieldChange(
                  "currentDesignation",
                  event.target.value
                )
              }
            />

            {renderFieldError("currentDesignation")}
          </div>

          {/* Highest Qualification */}
          <div className="space-y-2">
            <Label htmlFor="candidate-qualification">
              Highest Qualification{" "}
              <span className="text-red-500">*</span>
            </Label>

            <Input
              ref={(element) => {
                fieldRefs.current.highestQualification = element;
              }}
              id="candidate-qualification"
              value={candidateProfile.highestQualification}
              placeholder="B.Tech, MBA, MCA..."
              className={getFieldClassName("highestQualification")}
              onChange={(event) =>
                onFieldChange(
                  "highestQualification",
                  event.target.value
                )
              }
            />

            {renderFieldError("highestQualification")}
          </div>

          {/* Portfolio - Optional */}
          <div className="space-y-2">
            <Label htmlFor="candidate-portfolio">
              Portfolio / GitHub{" "}
              <span className="text-slate-400">(Optional)</span>
            </Label>

            <Input
              ref={(element) => {
                fieldRefs.current.portfolioUrl = element;
              }}
              id="candidate-portfolio"
              type="url"
              value={candidateProfile.portfolioUrl}
              placeholder="https://github.com/username"
              className={getFieldClassName("portfolioUrl")}
              onChange={(event) =>
                onFieldChange("portfolioUrl", event.target.value)
              }
            />

            {renderFieldError("portfolioUrl")}
          </div>

          {/* Key Skills - Optional */}
          <div className="space-y-2">
            <Label htmlFor="candidate-skills">
              Key Skills
            </Label>

            <Textarea
              id="candidate-skills"
              value={candidateProfile.skills}
              placeholder="React, Node.js, TypeScript, PostgreSQL..."
              rows={4}
              onChange={(event) =>
                onFieldChange("skills", event.target.value)
              }
            />
          </div>
        </div>

        {/* Fallback general error */}
        {profileError &&
          Object.keys(profileFieldErrors).length === 0 && (
            <div className="mt-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{profileError}</span>
            </div>
          )}

        <div className="mt-8 flex items-center justify-between gap-3 border-t pt-5">
          <Button type="button" variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>

          <Button type="button" onClick={onNext}>
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}